import type {
  TaskListResponse,
  TaskStatus,
} from '../../services/api/types.gen';

export type LockedTaskType = 'order' | 'visit' | 'patient_communication';
export type ScheduleLockedTask = Pick<
  TaskListResponse,
  'task_type' | 'status'
> & {
  task_type: LockedTaskType;
};

export const LOCKED_ORDER_STATUSES = new Set<TaskStatus>([
  'completed',
  'sent_to_physician',
  'submitted_with_signature',
  'returned_with_signature',
]);

export const LOCKED_VISIT_STATUSES = new Set<TaskStatus>([
  'submitted_with_signature',
  'pending_clinician_signature',
  'completed',
]);

export const LOCKED_PATIENT_COMMUNICATION_STATUSES = new Set<TaskStatus>([
  'completed',
]);

const ORDER_TOOLTIP_BY_STATUS: Partial<Record<TaskStatus, string>> = {
  completed:
    'Locked because this order is completed and ready for checkout. Use Unlock & Edit from the order detail panel to make changes.',
  sent_to_physician:
    'Locked because this order was sent for physician signature. Use Unlock & Edit from the order detail panel to make changes.',
  submitted_with_signature:
    'Locked because this order already has a signature. Use Unlock & Edit from the order detail panel to make changes.',
  returned_with_signature:
    'Locked because this order was returned with a signature. Use Unlock & Edit from the order detail panel to make changes.',
};

const VISIT_TOOLTIP_BY_STATUS: Partial<Record<TaskStatus, string>> = {
  submitted_with_signature:
    'Locked because this visit already has a signature. Use Edit from the visit detail panel to make changes.',
  pending_clinician_signature:
    'Locked because this visit has been signed by the case manager and needs clinician re-signature.',
  completed: 'Locked because this visit has been approved and completed.',
};

const PATIENT_COMMUNICATION_TOOLTIP_BY_STATUS: Partial<
  Record<TaskStatus, string>
> = {
  completed: 'Locked because this patient communication is completed.',
};

const FALLBACK_LOCKED_TASK_TOOLTIP =
  'Locked task. Open the task detail panel to review available edit actions.';

export function isLockedOrderStatus(
  status: TaskStatus | null | undefined
): boolean {
  return status ? LOCKED_ORDER_STATUSES.has(status) : false;
}

export function isLockedVisitStatus(
  status: TaskStatus | null | undefined
): boolean {
  return status ? LOCKED_VISIT_STATUSES.has(status) : false;
}

export function isLockedPatientCommunicationStatus(
  status: TaskStatus | null | undefined
): boolean {
  return status ? LOCKED_PATIENT_COMMUNICATION_STATUSES.has(status) : false;
}

export function isScheduleLockedTask(
  task: Pick<TaskListResponse, 'task_type' | 'status'>
): task is ScheduleLockedTask {
  if (task.task_type === 'order') return isLockedOrderStatus(task.status);
  if (task.task_type === 'visit') return isLockedVisitStatus(task.status);
  if (task.task_type === 'patient_communication') {
    return isLockedPatientCommunicationStatus(task.status);
  }
  return false;
}

export function getScheduleLockedTask(
  task: Pick<TaskListResponse, 'task_type' | 'status'>
): ScheduleLockedTask | null {
  return isScheduleLockedTask(task) ? task : null;
}

export function getLockedTaskTooltip(
  taskType: LockedTaskType,
  status: TaskStatus | null | undefined
): string {
  if (!status) return FALLBACK_LOCKED_TASK_TOOLTIP;
  const copyByStatus =
    taskType === 'order'
      ? ORDER_TOOLTIP_BY_STATUS
      : taskType === 'visit'
        ? VISIT_TOOLTIP_BY_STATUS
        : PATIENT_COMMUNICATION_TOOLTIP_BY_STATUS;
  return copyByStatus[status] ?? FALLBACK_LOCKED_TASK_TOOLTIP;
}
